import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowLeft } from 'lucide-react';

/**
 * Minimal Persistent Mobile Header
 * TOP LEFT: Eternity logo
 * TOP RIGHT: Menu button
 * Safe-area aware, transparent overlay
 */
export default function MobileHeader({ canGoBack, onGoBack, onNavigate }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuNavigate = (destination) => {
    setMenuOpen(false);
    if (onNavigate) {
      onNavigate(destination);
    }
  };

  return (
    <>
      {/* TOP HEADER OVERLAY */}
      <div 
        className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        {/* Subtle top gradient for contrast */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 via-black/20 to-transparent" />
        
        {/* Header content */}
        <div className="relative flex items-center justify-between px-4 py-3 pointer-events-auto">
          {/* LEFT: Logo or Back */}
          <div className="flex items-center gap-2">
            {canGoBack && (
              <button
                onClick={onGoBack}
                className="p-2 -ml-2 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-white drop-shadow-lg" strokeWidth={2.5} />
              </button>
            )}
            
            {/* Eternity Logo - WHITE for visibility on dark backgrounds */}
            <div className="flex items-center">
              <img 
                src="/logos/eternity-symbol.png" 
                alt="Eternity" 
                className="h-8 w-auto"
                style={{ 
                  filter: 'brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.6))'
                }}
              />
            </div>
          </div>

          {/* RIGHT: Menu Button */}
          <button
            onClick={() => setMenuOpen(true)}
            className="p-2.5 hover:opacity-80 active:opacity-60 transition-opacity"
            style={{ minWidth: '44px', minHeight: '44px' }}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5 text-white drop-shadow-lg" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* MENU OVERLAY */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* Close button */}
            <div className="absolute top-0 right-0 p-4" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 transition-all"
                style={{ minWidth: '44px', minHeight: '44px' }}
                aria-label="Close menu"
              >
                <X className="w-6 h-6 text-white" strokeWidth={2.5} />
              </button>
            </div>

            {/* Menu content */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="flex flex-col items-center justify-center h-full px-8"
            >
              {/* Eternity branding - WHITE for menu overlay */}
              <div className="mb-12">
                <img 
                  src="/logos/eternity-symbol.png" 
                  alt="Eternity" 
                  className="h-16 w-auto mx-auto mb-4"
                  style={{ filter: 'brightness(0) invert(1)', opacity: 0.6 }}
                />
              </div>

              {/* Navigation items */}
              <nav className="flex flex-col items-center gap-6 w-full max-w-xs">
                <MenuButton onClick={() => handleMenuNavigate('home')}>
                  HOME
                </MenuButton>
                <MenuButton onClick={() => handleMenuNavigate('work')}>
                  OUR WORK
                </MenuButton>
                <MenuButton onClick={() => handleMenuNavigate('systems')}>
                  SYSTEMS
                </MenuButton>
                <MenuButton onClick={() => handleMenuNavigate('portfolio')}>
                  PORTFOLIO
                </MenuButton>
                <MenuButton onClick={() => handleMenuNavigate('about')}>
                  ABOUT
                </MenuButton>
                <MenuButton onClick={() => handleMenuNavigate('build')}>
                  BUILD WITH US
                </MenuButton>

                {/* Separator */}
                <div className="w-12 h-px bg-white/20 my-2" />

                <MenuButton onClick={() => window.location.href = '/intelligence'} secondary>
                  TEAM PORTAL
                </MenuButton>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Menu Button Component
 */
function MenuButton({ children, onClick, secondary = false }) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full py-4 px-6 rounded-lg text-center
        font-light tracking-widest transition-all
        ${secondary 
          ? 'text-white/60 text-sm hover:text-white hover:bg-white/5' 
          : 'text-white text-base hover:bg-white/10'
        }
        active:scale-95
      `}
      style={{ minHeight: '44px' }}
    >
      {children}
    </button>
  );
}
